import { useEffect, useState } from 'react';
import { ModalOverlay } from './ModalOverlay';
import { ModalPanel } from './ModalPanel';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** 'center'（既定）は中央モーダル、'drawer' は画面右からハーフスライドして表示する。 */
  variant?: 'center' | 'drawer';
}

export function Modal({ isOpen, onClose, title, children, size = 'md', variant = 'center' }: ModalProps) {
  // ドロワー表示時の右からのスライドイン制御（開いた直後に translate を 0 にして滑り込ませる）
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) { setShown(false); return; }
    const raf = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(raf);
  }, [isOpen]);

  if (!isOpen) return null;

  if (variant === 'drawer') {
    return (
      <div role="dialog" aria-modal="true" aria-labelledby="modal-title" className="fixed inset-0 z-50 flex justify-end">
        <ModalOverlay onClick={onClose} />
        <div
          className={`relative z-50 h-full w-full sm:w-1/2 sm:min-w-[480px] bg-white shadow-xl flex flex-col transform transition-transform duration-300 ease-out ${shown ? 'translate-x-0' : 'translate-x-full'}`}
        >
          <div className="flex-none flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h3 id="modal-title" className="text-lg font-semibold text-gray-900">{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="閉じる"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
        </div>
      </div>
    );
  }

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="modal-title" className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <ModalOverlay onClick={onClose} />
      <div className="relative z-50 w-full flex justify-center">
        <ModalPanel title={title} size={size} onClose={onClose}>
          {children}
        </ModalPanel>
      </div>
    </div>
  );
}
